import { useEffect, useRef, useState } from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  CHECK_FOR_UPDATES_EVENT,
  CHECK_FOR_UPDATES_RESULT_EVENT,
  forceDeviceUpdateRefresh,
  type CheckForUpdatesResult,
} from "@/components/AppUpdatePrompt";

type Props = {
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  className?: string;
  label?: string;
};

export default function CheckForUpdatesButton({
  variant = "ghost",
  size = "sm",
  className,
  label = "Check for updates",
}: Props) {
  const [checking, setChecking] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const onResult = (e: Event) => {
      if (!checking) return;
      const detail = (e as CustomEvent<CheckForUpdatesResult>).detail;
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      toast.info("Clearing cached app files and refreshing…");
      void forceDeviceUpdateRefresh();
    };
    window.addEventListener(CHECK_FOR_UPDATES_RESULT_EVENT, onResult);
    return () => {
      window.removeEventListener(CHECK_FOR_UPDATES_RESULT_EVENT, onResult);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [checking]);

  const handleClick = () => {
    if (checking) return;
    setChecking(true);
    window.dispatchEvent(new CustomEvent(CHECK_FOR_UPDATES_EVENT));
    timeoutRef.current = window.setTimeout(() => {
      toast.info("Refreshing to clear cached app files…");
      void forceDeviceUpdateRefresh();
    }, 8000);
  };

  return (
    <Button variant={variant} size={size} className={className} onClick={handleClick} disabled={checking}>
      <RefreshCw className={`w-4 h-4 mr-2 ${checking ? "animate-spin" : ""}`} />
      {checking ? "Checking…" : label}
    </Button>
  );
}
